import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Input,
  Button,
  Form,
  Tooltip,
  Spin,
  Modal,
  Pagination,
  message
} from 'antd'
import 'antd/dist/antd.css'
import './NanoBananAi.scss'
import './AiChat.scss'
import { ImagePreview } from '../../../../../../../../../components'
import { api } from '../../../../../../../../../services'
import dummyUserImg from '../../../../../../../../../assets/user.svg'
import { useSelector } from 'react-redux'
// import { GoogleGenAI } from '@google/genai';
import { FiMessageSquare, FiInfo, FiSearch, FiImage } from 'react-icons/fi'
import { PopupMedia } from '../../../../../../../../Library/internal'
import _ from 'lodash'
import imageCompression from 'browser-image-compression'
import { useTranslation } from 'react-i18next'
import AspectRatioSelector from '../AiImages/AspectRatioSelector'
import PixelSearchModal from './PixelSearchModal'
import AiChat from './AiChat'
import AiImagesForm from './AiImagesForm'
// const GEMINI_API_KEY = process.env.REACT_APP_NANO_BANANA_API_KEY;
// const ai = new GoogleGenAI({ apiKey: GEMINI_API_KEY });

const NanoBananAi = ({
  form,
  onImage,
  selectedImage,
  isUploading,
  isBackground = false,
  isCover = false,
  pageBuilder = {}
}) => {
  const { getFieldDecorator, setFieldsValue } = form

  const [promptText, setPromptText] = useState('')
  const [promptTitle, setPromptTitle] = useState('')
  const [includeText, setIncludeText] = useState(false)
  const [mediaLoading, setMediaLoading] = useState(false)
  const [generationStatus, setGenerationStatus] = useState({
    status: 'idle',
    percentage: 0,
    isVariation: false
  })
  const [currentGeneratedImage, setCurrentGeneratedImage] = useState({
    info: '',
    url: ''
  })
  const [refImages, setRefImages] = useState([]) // {id, preview, base64, mime, sizeMB}
  const endOfPagesRef = useRef(null)
  const [activeTab, setActiveTab] = useState(isCover ? 'text-to-image' : 'image-edit') // 'image-edit' | 'text-to-image'
  const [showPromptHelper, setShowPromptHelper] = useState(false)
  const [showPixelSearchModal, setShowPixelSearchModal] = useState(false)
  const [showMediaLibrary, setShowMediaLibrary] = useState(false)

  const { t } = useTranslation()

  useEffect(() => {
    if (
      generationStatus.status === 'waiting-to-start' ||
      generationStatus.status === 'running' ||
      generationStatus.status === 'complete'
    ) {
      goToEndOfPages()
    }
  }, [generationStatus.status])

  useEffect(() => {
    const { contentId, content } = pageBuilder
    if (contentId && content) {
      handleMediaSelect({ id: contentId, url: content })
    }
  }, [pageBuilder])

  useEffect(() => {
    if (
      generationStatus.status === 'waiting-to-start' ||
      generationStatus.status === 'running' ||
      generationStatus.status === 'complete'
    ) {
      goToEndOfPages()
    }
  }, [generationStatus.status])

  // Trigger validation when Image Edit tab is active and no images are present
  useEffect(() => {
    if (activeTab === 'image-edit' && refImages.length === 0) {
      setTimeout(() => {
        form.validateFields(['referenceImages'], () => { })
      }, 100)
    }
  }, [activeTab, refImages.length, form])

  const goToEndOfPages = () => {
    if (endOfPagesRef.current) {
      endOfPagesRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }

  const handleGenerateImage = (prompt = '') => {
    // Always validate form fields first
    form.validateFields((errors, values) => {
      if (errors) {
        // Log errors for debugging
        console.log('Validation errors:', errors)
        return
      }

      // Additional check for Image Edit mode
      if (activeTab === 'image-edit' && refImages.length === 0) {
        message.error(t('v5:please_upload_reference_image'))
        return
      }

      if (!prompt && !promptText) {
        return
      }

      startGeneration(prompt)
    })
  }

  const generateImageWithGemini = async (contents, options = {}) => {
    const { aspectRatio, processedImages = [] } = options
    try {
      setGenerationStatus({
        status: 'running',
        percentage: 0,
        isVariation: false
      })

      // Build the new payload format
      const payload = {
        generate_infographic: false,
        user_prompt: contents[0]?.parts?.[0]?.text || '',
        aspect_ratio: aspectRatio || '1:1'
      }

      // Add inline_images if there are reference images
      if (processedImages && processedImages.length > 0) {
        payload.inline_images = processedImages.map(img => ({
          data: img.base64,
          mime_type: img.mime || 'image/png'
        }))
      }

      const response = await api.imageGeneration.generateNanoBananaImage(payload)

      if (response?.data) {
        // Check if response is a blob (direct image)
        const isBlob = response.data instanceof Blob ||
          (response.data.type && typeof response.data.size === 'number' && !response.data.body);

        if (isBlob) {
          // Check if the blob is actually a JSON response (error or new format)
          if (response.data.type === 'application/json') {
            const text = await new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result)
              reader.onerror = reject
              reader.readAsText(response.data)
            })

            try {
              const json = JSON.parse(text)

              // Handle new format with direct URL
              if (json.url) {
                try {
                  const imgResponse = await fetch(json.url)
                  const blob = await imgResponse.blob()
                  const reader = new FileReader()
                  reader.onloadend = () => {
                    setGenerationStatus({
                      status: 'complete',
                      percentage: 100,
                      isVariation: false
                    })
                    setCurrentGeneratedImage({ info: '', url: reader.result })
                  }
                  reader.onerror = () => {
                    throw new Error('Failed to read image data')
                  }
                  reader.readAsDataURL(blob)
                  return
                } catch (err) {
                  console.error('Error fetching generated image:', err)
                  throw new Error('Failed to fetch generated image from URL')
                }
              }

              if (json.errorMessage) {
                message.error(json.errorMessage)
                setGenerationStatus({
                  status: 'error',
                  percentage: 0,
                  isVariation: false
                })
                return
              }
            } catch (e) {
              console.error('Failed to parse error response:', e)
            }
          }

          // Convert blob to data URL
          const reader = new FileReader()
          reader.onloadend = () => {
            setGenerationStatus({
              status: 'complete',
              percentage: 100,
              isVariation: false
            })
            setCurrentGeneratedImage({ info: '', url: reader.result })
          }
          reader.onerror = () => {
            throw new Error('Failed to read image data')
          }
          reader.readAsDataURL(response.data)
        } else if (response.data.body || response.data.errorMessage) {
          // Handle old format (JSON with base64) - for backward compatibility
          const { body, headers, errorMessage } = response.data

          if (errorMessage) {
            message.error(errorMessage)
            setGenerationStatus({
              status: 'error',
              percentage: 0,
              isVariation: false
            })
            return
          }

          if (!body) {
            throw new Error('No image body in response')
          }

          const contentType = headers?.['Content-Type'] || 'image/png'
          const dataUrl = `data:${contentType};base64,${body}`
          setGenerationStatus({
            status: 'complete',
            percentage: 100,
            isVariation: false
          })
          setCurrentGeneratedImage({ info: '', url: dataUrl })
        } else {
          console.error('Unexpected response format:', response.data)
          throw new Error('Unexpected response format')
        }
        return
      }
    } catch (error) {
      message.error(error?.error?.message || t('v5:error_generating_image'))
      setGenerationStatus({
        status: 'error',
        percentage: 0,
        isVariation: false
      })
    }
  }

  const startGeneration = async value => {
    let prompt = value || promptText
    if (promptTitle) {
      prompt = `${prompt} with text "${promptTitle}"`
    }

    form.validateFields(async (errors, values) => {
      if (errors) {
        console.log('Form validation errors:', errors)
        return
      }

      // Double-check for image edit mode
      if (activeTab === 'image-edit' && refImages.length === 0) {
        message.error(t('v5:please_upload_reference_image'))
        return
      }

      let finalPrompt = prompt

      const selectedAspectRatio = isCover ? "16:9" : values.aspectRatio

      // ✅ Compress images to keep total under ~5 MB
      let processedImages = refImages
      if (activeTab === 'image-edit' && refImages && refImages.length) {
        const totalTargetMB = 3 // total size budget
        const perImageTargetMB = totalTargetMB / 9 // fixed divisor for safety

        processedImages = await Promise.all(
          refImages.map(async (img, index) => {
            try {
              const dataUrl = img.base64.startsWith('data:')
                ? img.base64
                : `data:${img.mime || 'image/png'};base64,${img.base64}`

              const fileFromDataUrl = await imageCompression.getFilefromDataUrl(
                dataUrl,
                `ref-${index + 1}.${(img.mime || 'image/png').split('/')[1] ||
                'png'}`
              )

              const compressedFile = await imageCompression(fileFromDataUrl, {
                maxSizeMB: perImageTargetMB,
                maxWidthOrHeight: 800,
                useWebWorker: true
              })

              const compressedBase64 = await imageCompression.getDataUrlFromFile(
                compressedFile
              )
              return {
                base64: compressedBase64.split(',')[1],
                mime: compressedFile.type || img.mime || 'image/png'
              }
            } catch (err) {
              console.error('Image compression failed:', err)
              return img // fallback to original if compression fails
            }
          })
        )
      }

      const contents = [
        {
          role: 'user',
          parts: [
            { text: finalPrompt },
            ...(activeTab === 'image-edit' &&
              processedImages &&
              processedImages.length
              ? processedImages.map(img => ({
                inlineData: {
                  data: img.base64,
                  mimeType: img.mime || 'image/png'
                },
                mediaResolution: {
                  level: 'media_resolution_high'
                }
              }))
              : [])
          ]
        }
      ]

      generateImageWithGemini(contents, {
        aspectRatio: selectedAspectRatio,
        processedImages: activeTab === 'image-edit' ? processedImages : []
      })
    })
  }

  const selectImage = () => {
    const linkToUse = currentGeneratedImage?.url
    if (!linkToUse) return
    if (selectedImage && selectedImage.link === linkToUse) {
      return
    }

    onImage({
      link: linkToUse,
      id: generate7DigitRandomInteger(),
      data: currentGeneratedImage || null
    })
  }

  function generate7DigitRandomInteger() {
    const min = 1000000
    const max = 9999999
    return Math.floor(Math.random() * (max - min + 1)) + min
  }

  const isImageBeingGenerated = useMemo(() => {
    return (
      generationStatus.status !== 'idle' &&
      generationStatus.status !== 'error' &&
      generationStatus.status !== 'complete'
    )
  }, [generationStatus])

  const handleGenerateFromAi = prompt => {
    setShowPromptHelper(false)
    setPromptText(prompt)
    setFieldsValue({ prompt: prompt })

    const promptInput = document.getElementById('promptInput')
    if (promptInput) {
      promptInput.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handlePixelImageSelect = async (image, isMedia) => {
    try {
      // Check if we've reached the limit of 9 images
      if (refImages.length >= 9) {
        message.warning(t('v5:max_reference_images_allowed'))
        setMediaLoading(false)
        return
      }

      const response = await fetch(
        image.url,
        isMedia ? { mode: 'no-cors' } : {}
      )
      let blob = {}
      if (isMedia) {
        const buffferRes = await api.library.getLibraryBuffer(image.id)

        const base64ToBlob = (base64Data, contentType = 'image/jpeg') => {
          const byteChars = atob(base64Data)
          const byteNumbers = new Array(byteChars.length)
          for (let i = 0; i < byteChars.length; i++) {
            byteNumbers[i] = byteChars.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          return new Blob([byteArray], { type: contentType })
        }

        blob = await base64ToBlob(buffferRes?.data, 'image/jpeg')
      } else {
        blob = await response.blob()
      }
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result
        if (typeof result === 'string') {
          const mimeType = blob.type || 'image/jpeg'
          const newRefImage = {
            id: Date.now() + Math.random(),
            preview: image.url,
            base64: result.split(',')[1] || '',
            mime: mimeType,
            sizeMB: (blob.size / (1024 * 1024)).toFixed(2)
          }
          const newRefImages = [...refImages, newRefImage]
          setRefImages(newRefImages)

          // Clear validation errors when images are added
          form.setFields({
            referenceImages: {
              value: newRefImages.length,
              errors: null
            }
          })
        }
      }
      reader.readAsDataURL(blob)
      setMediaLoading(false)
    } catch (error) {
      setMediaLoading(false)
      message.error(t('v5:failed_to_convert_image_url'))
    }
  }

  const handleMediaSelect = async image => {
    setMediaLoading(true)
    handlePixelImageSelect(image, true)
    setTimeout(() => {
      setShowMediaLibrary(false)
    }, 500)
  }

  const handleInsertData = (privateMedia, publicMedia, orgMedia) => {
    let tempData = []
    if (privateMedia) {
      const { rows: privateData } = privateMedia
      privateData.forEach(data => {
        tempData.push(data)
      })
    }

    if (publicMedia) {
      const { rows: publicData } = publicMedia
      publicData.forEach(publicData => {
        tempData.push(publicData)
      })
    }

    if (orgMedia) {
      const { rows: orgData } = orgMedia
      orgData.forEach(orgData => {
        tempData.push(orgData)
      })
    }

    const item = _.head(tempData.filter(value => value.isSelected))
    item.url = item.link
    handleMediaSelect(item)
  }

  return (
    <div className="nbai-container">
      <div className="nbai-left">
        <div className="nbai-card">
          <div className="nbai-card-header">
            <div className="nbai-form-header">
              <div className="nbai-form-sub">
                ⚡ {t('v4:ai_image_generation')}
              </div>
              <Button
                size="small"
                type="link"
                className="nbai-link-btn"
                onClick={() => setShowPromptHelper(prev => !prev)}
              >
                <FiMessageSquare />
                {showPromptHelper
                  ? t('v5:hide_prompt_helper')
                  : t('v5:prompt_helper')}
              </Button>
            </div>
          </div>
          <div className="nbai-tabs">
            <div
              className={`nbai-tab ${activeTab === 'image-edit' ? 'nbai-tab--active' : ''
                }`}
              onClick={() => {
                setActiveTab('image-edit')
                // Clear any existing validation errors first, then validate
                form.resetFields(['referenceImages'])
                setTimeout(() => {
                  if (refImages.length === 0) {
                    form.validateFields(['referenceImages'], () => { })
                  }
                }, 100)
              }}
            >
              {t('v4:image_edit')}
            </div>
            <div
              className={`nbai-tab ${activeTab === 'text-to-image' ? 'nbai-tab--active' : ''
                }`}
              onClick={() => setActiveTab('text-to-image')}
            >
              {t('v4:text_to_image')}
            </div>
          </div>
          <div className="nbai-section">
            <div className="nbai-uploader">
              <AiImagesForm
                isCover={isCover}
                isUploading={isUploading}
                mediaLoading={mediaLoading}
                form={form}
                getFieldDecorator={getFieldDecorator}
                promptText={promptText}
                isImageBeingGenerated={isImageBeingGenerated}
                handleGenerateImage={handleGenerateImage}
                setPromptText={setPromptText}
                promptTitle={promptTitle}
                setPromptTitle={setPromptTitle}
                generationStatus={generationStatus}
                includeText={includeText}
                setIncludeText={setIncludeText}
                isBackground={isBackground}
                refImages={refImages}
                setRefImages={setRefImages}
                showUploader={activeTab === 'image-edit'}
                showPromptHelper={showPromptHelper}
                onTogglePromptHelper={() => setShowPromptHelper(prev => !prev)}
                setShowMediaLibrary={() => setShowMediaLibrary(prev => !prev)}
                onOpenPixelSearch={() => setShowPixelSearchModal(true)}
                activeTab={activeTab}
                promptHelperNode={
                  showPromptHelper ? (
                    <AiChat
                      generateImage={handleGenerateFromAi}
                      isImageBeingGenerated={isImageBeingGenerated}
                      autoStart={showPromptHelper}
                    />
                  ) : null
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="nbai-right">
        <div className="nbai-card">
          <div className="nbai-card-header">{t('v4:output_gallary')}</div>
          {(generationStatus.status === 'running' ||
            generationStatus.status === 'waiting-to-start') && (
              <div className="nbai-output-inline-progress">
                {generationStatus.status === 'waiting-to-start' && (
                  <div className="nbai-inline-spin">
                    <Spin
                      size="small"
                      tip={
                        generationStatus.isVariation
                          ? t('v5:preparing_variations')
                          : t('v5:preparing_image')
                      }
                    />
                  </div>
                )}
              </div>
            )}
          <div className="nbai-output">
            {!currentGeneratedImage.url && (
              <div className="nbai-output-empty">
                <div className="nbai-output-empty-title">
                  {t('v4:instant_generation')}
                </div>
                <div className="nbai-output-empty-sub">
                  {t('v4:prompt_and_unleash_the_power')}
                </div>
              </div>
            )}

            {currentGeneratedImage.url && (
              <div className="nbai-output-preview">
                <ImagePreview
                  src={currentGeneratedImage.url}
                  alt={t('v5:generated_image_alt')}
                />
                <div
                  style={{
                    marginTop: 12,
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <Button
                    type="primary"
                    onClick={selectImage}
                    loading={isUploading}
                  >
                    {t('v4:use_this_image')}
                  </Button>
                </div>
              </div>
            )}

            {(generationStatus.status === 'running' ||
              generationStatus.status === 'waiting-to-start') && (
                <div className="nbai-loading-overlay">
                  <div className="nbai-loader">
                    <div className="nbai-ring" />
                    <h3>{t('v5:processing_your_request')}</h3>
                    <p>
                      {generationStatus.isVariation
                        ? t('v5:generating_variations_in_progress')
                        : t('v5:lightning_fast_generation_in_progress')}
                    </p>
                    <div className="nbai-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}

            {generationStatus.status === 'error' && (
              <p>{t('v4:error_generating')}</p>
            )}
          </div>
        </div>

        <div className="nbai-tips">
          <div className="nbai-card">
            <div className="nbai-card-header">{t('v4:generation_tips')}</div>
            <div className="nbai-card-body">
              {t('v4:detailed_descriptions_for_more_accurate_results')}
            </div>
          </div>
        </div>
      </div>
      <div ref={endOfPagesRef} />
      <Modal
        visible={showMediaLibrary}
        onCancel={() => setShowMediaLibrary(false)}
        footer={null}
        width={'80%'}
        centered
      >
        <div className="wrap_modal_tabs">
          <PopupMedia
            isPageBuilder
            handleInsertData={handleInsertData}
            types="images"
          />
        </div>
      </Modal>
      <PixelSearchModal
        visible={showPixelSearchModal}
        onClose={() => setShowPixelSearchModal(false)}
        onImageSelect={handlePixelImageSelect}
      />
    </div>
  )
}

export default Form.create()(NanoBananAi)
